import React, { useState } from "react";
import SimpleCatalogPanel from "@/components/SimpleCatalogPanel";
import { useI18n } from "@/context/I18nContext";
import { DASHBOARD } from "@/constants/testIds";

/**
 * Catalogs Hub — the V2 admin entry point that exposes every "simple"
 * editable catalog (brands, suppliers, sizes, colors, materials,
 * units of measure, item types, decoration methods, logo locations).
 *
 * All tabs render the same generic <SimpleCatalogPanel> with different
 * resource/title/testid props. Adding a 10th catalog is a single line
 * in the TABS array.
 */
const TABS = [
  { key: "brands", resource: "brands", labelKey: "cat_brands_title", testIdPrefix: "brand" },
  { key: "suppliers", resource: "suppliers", labelKey: "cat_suppliers_title", testIdPrefix: "supplier" },
  { key: "item-types", resource: "item-types", labelKey: "cat_item_types_title", testIdPrefix: "itemtype" },
  { key: "units-of-measure", resource: "units-of-measure", labelKey: "cat_uom_title", testIdPrefix: "uom" },
  { key: "sizes", resource: "sizes", labelKey: "cat_sizes_title", testIdPrefix: "size" },
  { key: "colors", resource: "colors", labelKey: "cat_colors_title", testIdPrefix: "color" },
  { key: "decoration-methods", resource: "decoration-methods", labelKey: "cat_decoration_methods_title", testIdPrefix: "decomethod" },
  { key: "logo-locations", resource: "logo-locations", labelKey: "cat_logo_locations_title", testIdPrefix: "logoloc" },
  { key: "materials", resource: "materials", labelKey: "cat_materials_title", testIdPrefix: "material" },
];

export default function CatalogsHubPanel({ onBack }) {
  const { t } = useI18n();
  const [active, setActive] = useState(TABS[0].key);

  const current = TABS.find((x) => x.key === active) || TABS[0];

  return (
    <div data-testid={DASHBOARD.catalogsHub} className="space-y-6">
      <section>
        <button
          data-testid="catalogs-hub-back-btn"
          onClick={onBack}
          className="font-mono-tech text-[0.65rem] tracking-wider text-[var(--text-secondary)] hover:text-black mb-3"
        >
          ← {t("back")}
        </button>
        <div className="label-overline mb-1">SET / 03</div>
        <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tighter">
          {t("catalogs_hub_title")}
        </h1>
        <p className="text-sm text-[var(--text-secondary)] mt-1">
          {t("catalogs_hub_subtitle")}
        </p>
      </section>

      <section className="border border-[var(--border)] bg-white">
        <div className="flex flex-wrap gap-px bg-[var(--border)] border-b border-[var(--border)]">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              data-testid={`catalogs-tab-${tab.key}`}
              onClick={() => setActive(tab.key)}
              className={`px-4 py-3 font-mono-tech text-xs tracking-wider transition-colors ${
                active === tab.key
                  ? "bg-black text-white"
                  : "bg-white hover:bg-[var(--bg)]"
              }`}
            >
              {t(tab.labelKey)}
            </button>
          ))}
        </div>
        <div className="p-5">
          <h2 className="font-display text-2xl font-bold tracking-tight mb-4">
            {t(current.labelKey)}
          </h2>
          <SimpleCatalogPanel
            key={current.key /* remount when tab changes for clean state */}
            resource={current.resource}
            title={t(current.labelKey)}
            testIdPrefix={current.testIdPrefix}
          />
        </div>
      </section>
    </div>
  );
}
